import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Button, Col, FormLabel, Nav, Row, Tab } from "react-bootstrap";
import FilterAccordion from "../../../components/Common/Filter/FilterAccordion";
import "./ChargeList.css";
import "../../../components/Common/Modal/ModalPageEvent.css";
import Api from "../../../../constants/Api";
import Common from "../../../../constants/Common";
import axiosInstance from "../../../../services/AxiosInstance";
import { useBranch } from "../../../../context/BranchContext";
import { SearchOperation } from "../../../../utils/ApiUtils";
import { convertDoubleToStringPTBR } from "../../../../utils/ConverterUtils";
import { formatDate, formatDateTime, formatDateToYYYYMMDD } from "../../../../utils/DateUtils";
import { errorAlert, warnAlert } from "../../../components/alert/defaultAlert";
import InputDate from "../../../components/Forms/Element/InputDate";
import InputText from "../../../components/Forms/Element/InputText";
import GridCustomBase from "../../../components/GridCustom/GridCustomBase";
import PageTitle from "../../../layouts/PageTitle";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import ModalPage from "../../../components/Common/Modal/ModalPage";
import ChargeHistoryTimeline from "./ChargeHistoryTimeline";
import PaymentLinkActions from "../../../components/Custom/Charge/PaymentLinkActions";
import OutsidePaymentAction from "../../../components/Custom/Charge/OutsidePaymentAction";

const DetailSection = ({ icon, title, subtitle, action, className = "", children }) => (
    <section className={`charge-detail__section ${className}`.trim()}>
        <div className="charge-detail__section-header">
            <div className="charge-detail__section-heading">
                <span className="charge-detail__section-icon">
                    <i className={icon}></i>
                </span>
                <div>
                    <h6>{title}</h6>
                    {subtitle && <p>{subtitle}</p>}
                </div>
            </div>
            {action && <div className="charge-detail__section-action">{action}</div>}
        </div>
        {children}
    </section>
);

const DetailItem = ({ label, value, mono = false, accent = false, breakWord = false }) => {
    const hasValue = value !== undefined && value !== null && value !== "";

    return (
        <div className="charge-detail__item">
            <span className="charge-detail__item-label">{label}</span>
            <strong
                className={[
                    "charge-detail__item-value",
                    mono ? "charge-detail__item-value--mono" : "",
                    accent ? "charge-detail__item-value--accent" : "",
                    breakWord ? "charge-detail__item-value--break" : ""
                ].filter(Boolean).join(" ")}
            >
                {hasValue ? value : "-"}
            </strong>
        </div>
    );
};


/** "2026-09-18" como data local; new Date("2026-09-18") seria meia-noite UTC e cairia no dia anterior. */
const parseApiDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

/**
 * Filtros que outra tela pode mandar pela URL para abrir a lista já numa cobrança, como o
 * "Abrir cobrança" dos estornos das Comissões: ?customer=Nome&dueDate=AAAA-MM-DD&status=REFUNDED.
 */
const readLinkedFilters = (search) => {
    const params = new URLSearchParams(search);
    return {
        customer: params.get("customer") || "",
        dueDate: parseApiDate(params.get("dueDate")),
        status: params.get("status") || ""
    };
};

const ChargeList = () => {
    const { t } = useTranslation();
    const location = useLocation();
    // Lido uma vez, na abertura: depois disso os filtros são do usuário.
    const [linkedFilters] = useState(() => readLinkedFilters(location.search));
    const { selectedBranch } = useBranch();
    const currentDate = new Date();
    const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedCharge, setSelectedCharge] = useState(null);
    const [activeDetailTab, setActiveDetailTab] = useState("details");
    // Cobranças são consultadas normalmente por competência mensal. A tela já abre
    // no mês corrente para evitar carregar e somar todo o histórico por padrão.
    const [startDate, setStartDate] = useState(linkedFilters.dueDate || currentMonthStart);
    const [endDate, setEndDate] = useState(linkedFilters.dueDate || currentDate);
    const [dateFilterField, setDateFilterField] = useState("dueDate");
    const [customerName, setCustomerName] = useState(linkedFilters.customer);
    const [status, setStatus] = useState(linkedFilters.status);
    const [pageable, setPageable] = useState({});
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);
    // Evita cards vazios no primeiro paint enquanto a consulta mensal é iniciada.
    const [showLoading, setShowLoading] = useState(true);

    const buildFilters = () => {
        const filters = [];

        if ((startDate && !endDate) || (!startDate && endDate)) {
            warnAlert(t("period_filter_requires_both_dates"));
            return null;
        }

        if (selectedBranch?.id) {
            filters.push({
                field: "customer.branch.id",
                value: selectedBranch.id,
                operation: SearchOperation.EQUAL
            });
        }

        if (startDate && endDate) {
            if (dateFilterField === "paymentDate") {
                filters.push({
                    field: "paymentDate",
                    value: formatDateToYYYYMMDD(startDate) + "T00:00:00",
                    operation: SearchOperation.GREATER_THAN_EQUAL
                });
                filters.push({
                    field: "paymentDate",
                    value: formatDateToYYYYMMDD(endDate) + "T23:59:59",
                    operation: SearchOperation.LESS_THAN_EQUAL
                });
            } else {
                filters.push({
                    field: "dueDate",
                    value: formatDateToYYYYMMDD(startDate),
                    operation: SearchOperation.GREATER_THAN_EQUAL
                });
                filters.push({
                    field: "dueDate",
                    value: formatDateToYYYYMMDD(endDate),
                    operation: SearchOperation.LESS_THAN_EQUAL
                });
            }
        }

        if (customerName) {
            filters.push({
                field: "customer.fullName",
                value: customerName,
                operation: SearchOperation.MATCH
            });
        }

        if (status) {
            filters.push({
                field: "chargeStatus",
                value: status,
                operation: SearchOperation.EQUAL
            });
        }

        return filters;
    };

    const executeSearch = (targetPage = page, targetSize = size) => {
        const filters = buildFilters();
        if (filters === null) {
            return;
        }

        const payload = {
            filters,
            page: targetPage,
            size: targetSize,
            orders: [{
                field: dateFilterField,
                direction: "DESC"
            }]
        };

        setShowLoading(true);
        Promise.all([
            axiosInstance.post(`${Api.subscription.charge}/pageable`, payload),
            axiosInstance.post(`${Api.subscription.charge}/summary`, payload)
        ])
            .then(([pageableResponse, summaryResponse]) => {
                setPageable(pageableResponse.data);
                setRows(pageableResponse.data.content || []);
                setSummary(summaryResponse.data || {});
            })
            .catch((error) => {
                errorAlert(error);
            })
            .finally(() => {
                setShowLoading(false);
            });
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        executeSearch(page, size);
    }, [page, size, selectedBranch]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const handleSearch = () => {
        if (page !== 0) {
            setPage(0);
            return;
        }

        executeSearch(0, size);
    };

    const handleClearFilters = () => {
        const today = new Date();
        setStartDate(new Date(today.getFullYear(), today.getMonth(), 1));
        setEndDate(today);
        setDateFilterField("dueDate");
        setCustomerName("");
        setStatus("");

        if (page !== 0) {
            setPage(0);
            return;
        }

        setTimeout(() => executeSearch(0, size), 0);
    };

    const getStatusLabel = (chargeStatus) => {
        const labelMap = {
            PENDING: "pending",
            PAID: "paid",
            OVERDUE: "overdue",
            CANCELED: "cancelled",
            CANCELLED: "cancelled",
            PROCESSING: "processing",
            REFUNDED: "refunded",
            FAILED: "failed",
            EXPIRED: "payment_link_expired"
        };
        return labelMap[chargeStatus] || chargeStatus;
    };

    const getStatusTone = (chargeStatus) => {
        const toneMap = {
            PENDING: "pending",
            PAID: "paid",
            OVERDUE: "overdue",
            CANCELED: "inactive",
            CANCELLED: "inactive",
            PROCESSING: "processing",
            REFUNDED: "neutral",
            FAILED: "overdue",
            // Tom de alerta, e não de erro: não entrou dinheiro, mas ninguém errou nada e o
            // caminho é gerar um link novo.
            EXPIRED: "pending"
        };
        return toneMap[chargeStatus] || "neutral";
    };

    const statusOptions = [
        { value: "PENDING", label: t("pending") },
        { value: "PAID", label: t("paid") },
        { value: "OVERDUE", label: t("overdue") },
        { value: "CANCELED", label: t("cancelled") },
        { value: "PROCESSING", label: t("processing") },
        // A cobrança estornada perde a data de pagamento; sem este status só era achada pelo nome.
        { value: "REFUNDED", label: t("refunded") },
        { value: "EXPIRED", label: t("payment_link_expired") }
    ];

    const dateFilterOptions = [
        { value: "dueDate", label: t("due_date") },
        { value: "paymentDate", label: t("payment_date") }
    ];

    // Traduz o método de pagamento para exibição e aplica um fallback legível quando não houver chave no locale.
    const getPaymentMethodLabel = (paymentMethod) => {
        if (!paymentMethod) {
            return "-";
        }

        const normalizedKey = paymentMethod.toLowerCase();
        const translated = t(normalizedKey);

        if (translated !== normalizedKey) {
            return translated;
        }

        return paymentMethod
            .toLowerCase()
            .split("_")
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ");
    };

    // Na baixa por fora vale a forma que o operador informou: paymentMethod é a forma da
    // cobrança no gateway (cartão, boleto), que o webhook regrava depois da baixa. A baixa feita
    // no painel do provedor não diz como o cliente pagou, então só leva a marca.
    const getChargePaymentMethodLabel = (charge) => {
        if (charge?.outsidePaymentMethod) {
            return `${getPaymentMethodLabel(charge.outsidePaymentMethod)} · ${t("paid_outside_provider")}`;
        }
        if (charge?.paidOutsideProvider) {
            return t("paid_outside_provider");
        }
        return getPaymentMethodLabel(charge?.paymentMethod);
    };

    const formatMoney = (amount) => `${t("currency_symbol")} ${amount != null ? convertDoubleToStringPTBR(amount) : "0,00"}`;
    // Identificação da assinatura que gerou a cobrança. Um mesmo cliente pode ter mais de um
    // contrato, às vezes no MESMO plano (caso do assinante que pagou um link e teve um segundo
    // contrato criado no provedor). Sem esse número, duas cobranças de planos homônimos ficam
    // indistinguíveis no detalhe e o operador não sabe se está vendo outra assinatura.
    const subscriptionLabel = selectedCharge?.subscription?.id
        ? `#${selectedCharge.subscription.id}`
        : null;
    // Explica, em português de operador, por que a cobrança está cancelada. A origem fica no
    // campo notes, que o backend preenche com o evento do provedor ("Asaas event: ...") ou com
    // o rastro da reconciliação - texto técnico demais para a tela.
    const buildCancellationHint = (charge) => {
        if (!charge || charge.chargeStatus !== "CANCELED") {
            return null;
        }

        const notes = (charge.notes || "").toUpperCase();
        if (notes.includes("PAYMENT_DELETED") || notes.includes("SUBSCRIPTION_DELETED")) {
            // Caso mais comum e mais confuso: excluíram a cobrança no painel do provedor. Se
            // criaram outra no lugar, ela só aparece aqui quando for paga.
            return t("charge_canceled_deleted_at_provider");
        }
        if (notes.includes("RECONCILIA")) {
            return t("charge_canceled_by_reconciliation");
        }
        return t("charge_canceled_generic");
    };
    const cancellationHint = buildCancellationHint(selectedCharge);
    const providerTransactionValue = selectedCharge?.providerTransactionId || selectedCharge?.providerChargeId || null;
    const hasProviderData = selectedCharge && (
        selectedCharge.subscription?.integrationId ||
        selectedCharge.providerChargeId ||
        providerTransactionValue ||
        selectedCharge.paymentLink ||
        selectedCharge.digitableLine ||
        selectedCharge.pixKey
    );
    const hasAttemptData = selectedCharge && (
        selectedCharge.attemptCount > 0 ||
        selectedCharge.attemptDate ||
        selectedCharge.errorMessage
    );

    const summaryCards = [
        {
            key: "total",
            label: t("charges"),
            value: summary.totalCharges ?? pageable.totalElements ?? 0,
            caption: `${t("subscription_charges_listed_amount")}: ${formatMoney(summary.totalAmount)}`,
            icon: "fa-solid fa-file-invoice-dollar",
            className: "charge-list__metric-card--total"
        },
        {
            key: "pending",
            label: t("receivable"),
            value: summary.pendingCharges ?? 0,
            caption: formatMoney(summary.pendingAmount),
            icon: "fa-regular fa-clock",
            className: "charge-list__metric-card--pending"
        },
        {
            key: "overdue",
            label: t("overdue"),
            value: summary.overdueCharges ?? 0,
            caption: formatMoney(summary.overdueAmount),
            icon: "fa-solid fa-triangle-exclamation",
            className: "charge-list__metric-card--overdue"
        },
        {
            key: "paid",
            label: t("paid"),
            value: summary.paidCharges ?? 0,
            caption: formatMoney(summary.paidAmount),
            icon: "fa-solid fa-wallet",
            className: "charge-list__metric-card--paid"
        },
        {
            key: "expired",
            label: t("expired_payment_links"),
            value: summary.expiredCharges ?? 0,
            caption: formatMoney(summary.expiredAmount),
            icon: "fa-solid fa-link-slash",
            className: "charge-list__metric-card--expired"
        }
    ];

    return (
        <>
            <ModalPage
                show={showDetailsModal}
                setShow={setShowDetailsModal}
                size="lg"
                centered
                dialogClassName="charge-detail-modal"
                title={
                    <>
                        <i className="fas fa-file-invoice-dollar me-2"></i>
                        {t("charge_details")}
                    </>
                }
                child={selectedCharge && (
                        <div className="charge-detail">
                            <section className={`charge-detail__hero charge-detail__hero--${getStatusTone(selectedCharge.chargeStatus)}`}>
                                <div>
                                    <h3 className="charge-detail__amount">{formatMoney(selectedCharge.amount)}</h3>
                                    <p className="charge-detail__hero-text">
                                        {selectedCharge.customer?.fullName || "-"}
                                        {selectedCharge.subscription?.plan?.name ? ` · ${selectedCharge.subscription.plan.name}` : ""}
                                        {subscriptionLabel ? ` · ${t("subscription_identification")} ${subscriptionLabel}` : ""}
                                    </p>
                                </div>

                                <div className="charge-detail__hero-side">
                                    <span className={`subscription-workspace__status-badge subscription-workspace__status-badge--${getStatusTone(selectedCharge.chargeStatus)}`}>
                                        {t(getStatusLabel(selectedCharge.chargeStatus))}
                                    </span>
                                    <div className="charge-detail__hero-meta">
                                        <span>
                                            <i className="fa-regular fa-calendar me-1"></i>
                                            {t("due_date")}: {selectedCharge.dueDate ? formatDate(selectedCharge.dueDate) : "-"}
                                        </span>
                                        <span>
                                            <i className="fa-solid fa-rotate me-1"></i>
                                            {t("cycle")}: {selectedCharge.cycleNumber ?? "-"}
                                        </span>
                                    </div>
                                </div>
                            </section>

                            {/* "Cancelado" sozinho não diz nada: o operador vê a cobrança cancelada
                                aqui e uma cobrança em aberto no painel do provedor, sem entender a
                                relação. Esta faixa explica o que aconteceu e o que esperar. */}
                            {cancellationHint && (
                                <div className="charge-detail__hint">
                                    <i className="fa-solid fa-circle-info me-2"></i>
                                    <span>{cancellationHint}</span>
                                </div>
                            )}

                            <Tab.Container
                                activeKey={activeDetailTab}
                                onSelect={(key) => setActiveDetailTab(key)}
                            >
                                <Nav variant="tabs" className="charge-detail__tabs">
                                    <Nav.Item>
                                        <Nav.Link eventKey="details">{t("details")}</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="history">{t("history")}</Nav.Link>
                                    </Nav.Item>
                                </Nav>

                                <Tab.Content className="charge-detail__tab-content">
                                    <Tab.Pane eventKey="details">
                                        <div className="charge-detail__grid">
                                            <DetailSection
                                                icon="fa-solid fa-user"
                                                title={t("customer_data")}
                                            >
                                    <div className="charge-detail__items-grid">
                                        <DetailItem label={t("customer")} value={selectedCharge.customer?.fullName} />
                                        <DetailItem label={t("phone")} value={selectedCharge.customer?.phone} />
                                        <DetailItem label={t("branch")} value={selectedCharge.customer?.branch?.name} />
                                        <DetailItem label={t("plan")} value={selectedCharge.subscription?.plan?.name} />
                                        <DetailItem label={t("subscription_identification")} value={subscriptionLabel} mono />
                                        <DetailItem
                                            label={t("subscription_started_at")}
                                            value={selectedCharge.subscription?.createdAt ? formatDateTime(selectedCharge.subscription.createdAt) : "-"}
                                        />
                                        <DetailItem label={t("cycle_number")} value={selectedCharge.cycleNumber} />
                                        <DetailItem label={t("created_at")} value={selectedCharge.createdAt ? formatDateTime(selectedCharge.createdAt) : "-"} />
                                    </div>
                                </DetailSection>

                                <DetailSection
                                    icon="fa-solid fa-file-invoice-dollar"
                                    title={t("charge_data")}
                                >
                                    <div className="charge-detail__items-grid">
                                        <DetailItem label={t("amount")} value={formatMoney(selectedCharge.amount)} accent />
                                        <DetailItem label={t("status")} value={t(getStatusLabel(selectedCharge.chargeStatus))} />
                                        <DetailItem label={t("due_date")} value={selectedCharge.dueDate ? formatDate(selectedCharge.dueDate) : "-"} />
                                        <DetailItem label={t("payment_date")} value={selectedCharge.paymentDate ? formatDateTime(selectedCharge.paymentDate) : "-"} />
                                        <DetailItem label={t("payment_method")} value={getChargePaymentMethodLabel(selectedCharge)} />
                                        <DetailItem label={t("payment_provider")} value={selectedCharge.paymentProvider} />
                                    </div>
                                </DetailSection>

                                {hasProviderData && (
                                    <DetailSection
                                        icon="fa-solid fa-link"
                                        title={t("payment_provider_data")}
                                        className="charge-detail__section--full"
                                    >
                                        <div className="charge-detail__items-grid">
                                            {/* Contrato no provedor: é por ele que o operador confere a assinatura
                                                no painel do Asaas quando o cliente tem mais de uma. */}
                                            {selectedCharge.subscription?.integrationId && (
                                                <DetailItem
                                                    label={t("provider_subscription_id")}
                                                    value={selectedCharge.subscription.integrationId}
                                                    mono
                                                    breakWord
                                                />
                                            )}
                                            {selectedCharge.providerChargeId && (
                                                <DetailItem
                                                    label={t("provider_charge_id")}
                                                    value={selectedCharge.providerChargeId}
                                                    mono
                                                    breakWord
                                                />
                                            )}
                                            {providerTransactionValue && (
                                                <DetailItem
                                                    label={t("provider_transaction_id")}
                                                    value={providerTransactionValue}
                                                    mono
                                                    breakWord
                                                />
                                            )}
                                            {selectedCharge.digitableLine && (
                                                <DetailItem
                                                    label={t("digitable_line")}
                                                    value={selectedCharge.digitableLine}
                                                    mono
                                                    breakWord
                                                />
                                            )}
                                            {selectedCharge.pixKey && (
                                                <DetailItem
                                                    label={t("pix")}
                                                    value={selectedCharge.pixKey}
                                                    mono
                                                    breakWord
                                                />
                                            )}
                                        </div>

                                        <div className="charge-detail__link-row">
                                            <PaymentLinkActions
                                                charge={selectedCharge}
                                                phone={selectedCharge.customer?.phone}
                                                compact
                                                onLinkGenerated={(paymentLink) => {
                                                    setSelectedCharge((prev) => ({ ...prev, paymentLink }));
                                                    // A listagem também exibe o link, então recarrega para
                                                    // a linha não ficar dizendo que ele não existe.
                                                    executeSearch();
                                                }}
                                                onPixEnabled={(paymentLink) => {
                                                    setSelectedCharge((prev) => ({
                                                        ...prev,
                                                        paymentLink,
                                                        paymentMethod: "PIX"
                                                    }));
                                                    executeSearch();
                                                }}
                                            />
                                            {/* Fica com as ações do provedor, logo depois do
                                                WhatsApp, porque a baixa também é avisada a ele. */}
                                            <OutsidePaymentAction
                                                charge={selectedCharge}
                                                branchId={selectedCharge.customer?.branch?.id}
                                                compact
                                                onRegistered={(updated) => {
                                                    // O endpoint devolve o DTO resumido (status, e não
                                                    // chargeStatus); só os campos da baixa mudam aqui.
                                                    setSelectedCharge((prev) => ({
                                                        ...prev,
                                                        chargeStatus: updated.status,
                                                        paymentDate: updated.paymentDate,
                                                        paidOutsideProvider: updated.paidOutsideProvider,
                                                        outsidePaymentMethod: updated.outsidePaymentMethod
                                                    }));
                                                    executeSearch();
                                                }}
                                            />
                                        </div>
                                    </DetailSection>
                                )}

                                {hasAttemptData && (
                                    <DetailSection
                                        icon="fa-solid fa-triangle-exclamation"
                                        title={t("payment_attempts")}
                                        className="charge-detail__section--warning charge-detail__section--full"
                                    >
                                        <div className="charge-detail__items-grid">
                                            <DetailItem label={t("attempt_count")} value={selectedCharge.attemptCount} />
                                            <DetailItem
                                                label={t("last_attempt")}
                                                value={selectedCharge.attemptDate ? formatDateTime(selectedCharge.attemptDate) : "-"}
                                            />
                                            <DetailItem
                                                label={t("error_message")}
                                                value={selectedCharge.errorMessage}
                                                breakWord
                                            />
                                        </div>
                                    </DetailSection>
                                )}

                                {selectedCharge.notes && (
                                    <DetailSection
                                        icon="fa-regular fa-note-sticky"
                                        title={t("notes")}
                                        className="charge-detail__section--full"
                                    >
                                        <p className="charge-detail__notes">{selectedCharge.notes}</p>
                                    </DetailSection>
                                )}
                                        </div>
                                    </Tab.Pane>

                                    <Tab.Pane eventKey="history">
                                        <ChargeHistoryTimeline
                                            key={selectedCharge.id}
                                            chargeId={selectedCharge.id}
                                            active={activeDetailTab === "history"}
                                            getStatusLabel={getStatusLabel}
                                            getStatusTone={getStatusTone}
                                        />
                                    </Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                        </div>
                    )}
            />

            <PageTitle activeMenu={t("charges")} motherMenu={t("subscriptions")} />

            <FilterAccordion onSearch={handleSearch} onClear={handleClearFilters}>
                <Row className="g-3">
                    <Col md={3}>
                        <FormLabel>{t("date_filter_by")}</FormLabel>
                        <CustomSelect
                            value={dateFilterOptions.find((option) => option.value === dateFilterField)}
                            options={dateFilterOptions}
                            onChange={(option) => setDateFilterField(option?.value || "dueDate")}
                        />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t(dateFilterField === "paymentDate" ? "payment_date_start" : "due_date_start")}</FormLabel>
                        <InputDate onChange={(value) => setStartDate(value)} selected={startDate} />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t(dateFilterField === "paymentDate" ? "payment_date_end" : "due_date_end")}</FormLabel>
                        <InputDate onChange={(value) => setEndDate(value)} selected={endDate} />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t("customer")}</FormLabel>
                        <InputText value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t("status")}</FormLabel>
                        <CustomSelect
                            value={statusOptions.find((option) => option.value === status) || null}
                            options={statusOptions}
                            onChange={(option) => setStatus(option?.value || "")}
                            placeholder={t("all")}
                            isClearable
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <div className="charge-list__metrics">
                {summaryCards.map((card) => (
                    <div key={card.key} className={`charge-list__metric-card ${card.className}`}>
                        <div className="charge-list__metric-head">
                            <div>
                                <span className="charge-list__metric-label">{card.label}</span>
                            </div>
                            <span className="charge-list__metric-icon">
                                <i className={card.icon}></i>
                            </span>
                        </div>
                        {showLoading ? (
                            <div className="charge-list__metric-loading" role="status">
                                <div className="charge-list__metric-skeleton" aria-hidden="true" />
                                <div className="charge-list__metric-skeleton charge-list__metric-skeleton--caption" aria-hidden="true" />
                                <span>Carregando indicadores…</span>
                            </div>
                        ) : (
                            <>
                                <p className="charge-list__metric-value">{card.value}</p>
                                <p className="charge-list__metric-caption">{card.caption}</p>
                            </>
                        )}
                    </div>
                ))}
            </div>

            <GridCustomBase
                        pageable={pageable}
                        setPage={setPage}
                        setSize={setSize}
                        showLoading={showLoading}
                    >
                        <table className="table table-list i-table style-1 mb-0 border-0 charge-list__table">
                            <thead>
                                <tr>
                                    <th>{t("customer")}</th>
                                    <th>{t("amount")}</th>
                                    <th>{t("due_date")}</th>
                                    <th>{t("payment_date")}</th>
                                    <th>{t("status")}</th>
                                    <th>{t("cycle")}</th>
                                    <th>{t("actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr
                                        key={row.id}
                                        className={row.chargeStatus === "OVERDUE"
                                            ? "charge-list__row--overdue"
                                            : row.chargeStatus === "CANCELED" || row.chargeStatus === "CANCELLED"
                                                ? "charge-list__row--inactive"
                                                : ""
                                        }
                                    >
                                        <td>
                                            <span className="subscription-workspace__cell-main">
                                                {row.customer?.fullName || "-"}
                                            </span>
                                            <span className="subscription-workspace__cell-detail">
                                                {row.paymentProvider || row.customer?.phone || "-"}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="subscription-workspace__cell-main">
                                                {t("currency_symbol")} {row.amount != null ? convertDoubleToStringPTBR(row.amount) : "0,00"}
                                            </span>
                                            <span className="subscription-workspace__cell-detail">
                                                {getChargePaymentMethodLabel(row)}
                                            </span>
                                        </td>
                                        <td>{row.dueDate ? formatDate(row.dueDate) : "-"}</td>
                                        <td>{row.paymentDate ? formatDateTime(row.paymentDate) : "-"}</td>
                                        <td>
                                            <span className={`subscription-workspace__status-badge subscription-workspace__status-badge--${getStatusTone(row.chargeStatus)}`}>
                                                {t(getStatusLabel(row.chargeStatus))}
                                            </span>
                                        </td>
                                        <td>{row.cycleNumber || "-"}</td>
                                        <td>
                                            <Button
                                                type="button"
                                                variant="light"
                                                size="sm"
                                                className={`charge-list__detail-button${
                                                    row.chargeStatus === "OVERDUE" || row.chargeStatus === "FAILED"
                                                        ? " charge-list__detail-button--overdue"
                                                        : row.chargeStatus === "CANCELED" || row.chargeStatus === "CANCELLED"
                                                            ? " charge-list__detail-button--inactive"
                                                            : ""
                                                }`}
                                                onClick={() => {
                                                    setSelectedCharge(row);
                                                    setActiveDetailTab("details");
                                                    setShowDetailsModal(true);
                                                }}
                                            >
                                                <i className="fas fa-ellipsis-vertical"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </GridCustomBase>
        </>
    );
};

export default ChargeList;
